import {Injectable} from '@angular/core';
import {checkIfASCII, checkIfGiveCharIsNumberOrLetter} from './CommonFunctions';
import {FormGroup} from '@angular/forms';
import {ChangeVO} from '../generate/ChangeVO';
import {ErrorVO} from '../generate/ErrorVO';

@Injectable({
  providedIn: 'root'
})
export class UtilService {

  constructor() {
  }

  checkIfEmpty(value: string): boolean {
    if (this.checkNull(value)) {
      return true;
    }
    return '' === value.trim();
  }

  checkIfEmptyList(value: string[]): boolean {
    if (this.checkNull(value)) {
      return true;
    }
    return value.length == 0;
  }

  checkNull(value: any): boolean {
    return value == null;
  }


  checkIfGivenCharisNumberOrLetter(char: string): boolean {
    if (this.checkIfEmpty(char)) {
      return false;
    }

    return checkIfGiveCharIsNumberOrLetter(char);
  }


  isASCII(str): boolean {
    return checkIfASCII(str);
  }

  getDisplayValue(value: string): string {
    if (this.checkIfEmpty(value)) {
      return '';
    }

    return value.trim();
  }

  returnNullIfEmpty(value: string) {
    if (this.checkIfEmpty(value)) {
      return null;
    }

    return value.trim();
  }

  /**
   * Removes all null and undefined properties from an object.
   * This ensures the API request only includes fields with actual values,
   * complying with OpenAPI 3.1 schema (Optional & Not Nullable).
   *
   * @param obj The object to clean
   * @returns A new object with null/undefined properties removed
   */
  removeNullProperties<T extends object>(obj: T): Partial<T> {
    if (obj == null) {
      return obj;
    }

    const cleaned: any = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];

        // Skip null and undefined values
        if (value !== null && value !== undefined) {
          // Recursively clean nested objects
          if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
            const cleanedNested = this.removeNullProperties(value);
            // Only include nested object if it has properties after cleaning
            if (Object.keys(cleanedNested).length > 0) {
              cleaned[key] = cleanedNested;
            }
          } else {
            cleaned[key] = value;
          }
        }
      }
    }

    return cleaned;
  }


  cloneArray(values: Array<any>): Array<any> {
    if (values == null) {
      values = [];
      return values;
    }
    return values.slice();

  }


  public defineCSS(operationType: string): string {
    if (operationType === 'NOOP') {
      return '';
    } else if (operationType === 'ADD') {
      return 'opp-added';
    } else if (operationType === 'UPDATE') {
      return 'opp-updated';
    } else if (operationType === 'REMOVE') {
      return 'opp-removed';
    }

    return '';
  }

  getChange(field: string, change: ChangeVO): ChangeVO {
    if (change == null) {
      return null;
    }
    for (const eachChange of change.changes) {
        if (eachChange.field === field) {
          return eachChange;
        }
    }

    return null;
  }

  getContactFieldChange(field: string, change: ChangeVO): ChangeVO {
    if (change == null) {
      return null;
    }

    for (const eachChange of change.changes) {
      if (field.indexOf('address-') > -1 && eachChange.field === 'address') {
        field = field.replace('address-', '');
        for (const addChange of eachChange.changes) {
          if (addChange.field === field) {
            return addChange;
          }
        }

      } else {
        if (eachChange.field === field) {
          return eachChange;
        }
      }
    }

    return null;
  }

  buildObjectFromChange(object: Array<any>, allChanges: ChangeVO) {
    for (const change of allChanges.changes) {
      if (change.operationType === 'NOOP') {
        object.push(change.oldVal);
      } else if (change.operationType === 'ADD') {
        object.push(change.newVal);
      } else if (change.operationType === 'UPDATE') {
        object.push(change.newVal);
      } else if (change.operationType === 'REMOVE') {
        object.push(change.oldVal);
      }
    }
  }

  public capatalizeFirstChar(val: string): string {
    if (this.checkIfEmpty(val)) {
      return '';
    }
    return val.charAt(0).toUpperCase() + val.slice(1);
  }

  displayValidationError(form: FormGroup, controlName: string) {
    return form.contains(controlName)
      && !form.get(controlName).valid
      && form.get(controlName).touched;
  }

  getFieldError(field: string, errors: ErrorVO): ErrorVO {
    if (errors != null && errors.errors != null) {
      for (const error of errors.errors) {
        if (field === error.field) {
          return error;
        }
      }
    }
    return null;
  }

  getFieldErrorMessages(field: string, errors: ErrorVO): string[] {
    return this.getFieldError(field, errors)?.errorMessages ?? [];
  }

  normalizeErrors(response: ErrorVO | ErrorVO[] | null | undefined): ErrorVO | ErrorVO[] | null {
    if (response == null) {
      return null;
    }

    if (Array.isArray(response)) {
      return response.length > 0 ? response : null;
    }

    const hasErrorMessages = Array.isArray(response.errorMessages) && response.errorMessages.length > 0;
    const hasErrors = Array.isArray(response.errors) && response.errors.length > 0;
    const hasField = response.field != null && response.field !== '';

    if (!hasErrorMessages && !hasErrors && !hasField) {
      return null;
    }

    return response;
  }

  /**
   * Extracts the first error message from SDK error responses.
   * SDK errors have format: { status: 400, detail: "{...nested JSON...}" }
   * The detail field contains stringified ErrorVO with nested errors array.
   *
   * @param error The SDK error object
   * @returns The first error message found, or null if none
   */
  extractSdkErrorMessage(error: any): string | null {
    if (error == null) {
      return null;
    }

    // Handle SDK error format with detail field containing stringified JSON
    if (error.detail && typeof error.detail === 'string') {
      try {
        const parsed = JSON.parse(error.detail);

        // Check for global error messages first
        if (parsed.errorMessages && Array.isArray(parsed.errorMessages) && parsed.errorMessages.length > 0) {
          return parsed.errorMessages[0];
        }

        // Check nested errors array for field-specific errors
        if (parsed.errors && Array.isArray(parsed.errors) && parsed.errors.length > 0) {
          const firstError = parsed.errors[0];
          if (firstError.errorMessages && Array.isArray(firstError.errorMessages) && firstError.errorMessages.length > 0) {
            return firstError.errorMessages[0];
          }
        }
      } catch (e) {
        // Not valid JSON, fall through
      }
    }

    // Standard fields to skip; remaining string-valued fields contain the actual errors.
    const standardFields = new Set(['type', 'title', 'status', 'detail', 'instance', 'traceId', 'spanId']);
    const extraMessages: string[] = Object.entries(error)
      .filter(([key, val]) => !standardFields.has(key) && typeof val === 'string')
      .map(([, val]) => val as string);
    if (extraMessages.length > 0) {
      return extraMessages.join(', ');
    }

    // Fallback to detail, message, or generic error
    return error.detail || error.message || 'An error occurred';
  }

  /**
   * Extracts all field-level messages from an RFC 7807 API error response.
   * Returns one string per error field, falling back to detail/title when no
   * field-level errors are present.
   */
  extractAllApiErrors(error: any): string[] {
    if (error == null) {
      return ['An error occurred'];
    }
    if (error.status >= 400 && error.status < 500) {
      // Handle errors array: [{field, message}]
      if (Array.isArray(error.errors) && error.errors.length > 0) {
        return error.errors.map((e: any) => e.field ? `${e.field}: ${e.message}` : e.message);
      }
      const SKIP = new Set(['type', 'title', 'status', 'detail', 'instance', 'traceId', 'spanId']);
      const fieldErrors = Object.keys(error).filter(key => !SKIP.has(key));
      if (fieldErrors.length > 0) {
        return fieldErrors.map(key => `${key}: ${error[key]}`);
      }
      return [error.detail || error.title || 'An error occurred'];
    }
    return [error.message || error.title || 'An error occurred'];
  }
}
